import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivityReservationService } from '../activity-reservation.service';
import { AlertService } from '@app/_services';
import { first } from 'rxjs/operators';
import { ActivityReservationFormComponent } from '../activity-reservation-form/activity-reservation-form.component';

@Component({
  selector: 'app-activity-reservation-list',
  templateUrl: './activity-reservation-list.component.html',
  styleUrls: ['./activity-reservation-list.component.scss'],
  standalone: false,
})
export class ActivityReservationListComponent implements OnInit {

  @Input()  activities: any[] = [];
  @Input()  providers:  any[] = [];
  @Output() showToastEvent = new EventEmitter<string>();

  reservations: any[] = [];
  loading = false;

  // Filters
  search     = '';
  fProp      = '';
  fPay       = '';
  fBook      = '';
  fDateFrom  = '';
  fDateTo    = '';

  // Orphan filter
  showOrphansOnly = false;

  // Sort
  sortCol = 'activityDate';
  sortAsc = true;

  /** Unique property names derived from actual loaded reservations */
  get uniqueProperties(): string[] {
    const seen = new Set<string>();
    this.reservations.forEach(r => { if (r.property) seen.add(r.property.trim()); });
    return Array.from(seen).sort();
  }

  constructor(
    private resService: ActivityReservationService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void { this.loadReservations(); }

  loadReservations() {
    this.loading = true;
    this.resService.findAll().pipe(first()).subscribe(
      res => {
        this.reservations = res.data.activityReservations || [];
        this.loading = false;
      },
      err => { this.alertService.error(err); this.loading = false; }
    );
  }

  /* ── Computed filtered + sorted list ── */
  get filtered(): any[] {
    let rows = [...this.reservations];
    if (this.showOrphansOnly) {
      const valid = this.validActivityIds;
      rows = rows.filter(r => {
        if (!r.activityIds) return false;
        return r.activityIds.split(',').filter(Boolean).some((id: string) => !valid.has(id.trim()));
      });
    }
    if (this.search) {
      const q = this.search.toLowerCase().trim();
      rows = rows.filter(r =>
        `${r.firstName} ${r.lastName}`.toLowerCase().includes(q) ||
        (r.reservationNumber || '').toLowerCase().includes(q) ||
        (r.unit || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q)
      );
    }
    if (this.fProp)  rows = rows.filter(r => (r.property      || '').trim().toLowerCase() === this.fProp.toLowerCase());
    if (this.fPay)   rows = rows.filter(r => (r.paymentStatus  || '').trim().toLowerCase() === this.fPay.toLowerCase());
    if (this.fBook)  rows = rows.filter(r => (r.bookingStatus  || '').trim().toLowerCase() === this.fBook.toLowerCase());
    if (this.fDateFrom) rows = rows.filter(r => r.activityDate >= this.fDateFrom);
    if (this.fDateTo)   rows = rows.filter(r => r.activityDate <= this.fDateTo);

    rows.sort((a, b) => {
      const av = a[this.sortCol] ?? '', bv = b[this.sortCol] ?? '';
      if (typeof av === 'number') return this.sortAsc ? av - bv : bv - av;
      return this.sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return rows;
  }

  get hasFilters(): boolean {
    return !!(this.search || this.fProp || this.fPay || this.fBook || this.fDateFrom || this.fDateTo || this.showOrphansOnly);
  }

  /** Returns the set of valid activityId strings from the loaded activities list. */
  private get validActivityIds(): Set<string> {
    const s = new Set<string>();
    this.activities.forEach(a => s.add(String(a.activityId)));
    return s;
  }

  /** Returns the orphaned IDs (not found in loaded activities) for a single reservation. */
  orphanIds(activityIds: string): string[] {
    if (!activityIds || !this.activities.length) return [];
    const valid = this.validActivityIds;
    return activityIds.split(',').filter(Boolean).map(id => id.trim()).filter(id => !valid.has(id));
  }

  /** Count of reservations that contain at least one orphaned activityId. */
  get orphanReservationCount(): number {
    if (!this.activities.length) return 0;
    const valid = this.validActivityIds;
    return this.reservations.filter(r => {
      if (!r.activityIds) return false;
      return r.activityIds.split(',').filter(Boolean).some((id: string) => !valid.has(id.trim()));
    }).length;
  }

  dismissOrphanFilter() {
    this.showOrphansOnly = false;
  }

  get totalDue()  { return this.filtered.reduce((s, r) => s + (r.amountDue  || 0), 0); }
  get totalPaid() { return this.filtered.reduce((s, r) => s + (r.amountPaid || 0), 0); }
  get totalBal()  { return this.totalDue - this.totalPaid; }

  clearFilters() {
    this.search = ''; this.fProp = ''; this.fPay = '';
    this.fBook  = ''; this.fDateFrom = ''; this.fDateTo = '';
    this.showOrphansOnly = false;
  }

  sort(col: string) {
    if (this.sortCol === col) { this.sortAsc = !this.sortAsc; }
    else { this.sortCol = col; this.sortAsc = true; }
  }

  sortIcon(col: string): string {
    if (this.sortCol !== col) return '⇅';
    return this.sortAsc ? '▲' : '▼';
  }

  /* ── Modal ── */
  openNew() {
    const cfg = this.dialogConfig(null);
    cfg.data = { title: 'New Reservation', reservation: null, activities: this.activities };
    const ref = this.dialog.open(ActivityReservationFormComponent, cfg);
    ref.componentInstance.doAdd.subscribe((r: any) => {
      this.reservations.unshift(r);
      this.reservations = [...this.reservations];
      this.showToastEvent.emit('Reservation created.');
      ref.close();
    });
  }

  openEdit(reservation: any) {
    const cfg = this.dialogConfig(reservation);
    const ref = this.dialog.open(ActivityReservationFormComponent, cfg);
    ref.componentInstance.doUpdate.subscribe((r: any) => {
      const i = this.reservations.findIndex(x => x.activityReservationId === r.activityReservationId);
      if (i > -1) this.reservations[i] = r;
      this.reservations = [...this.reservations];
      this.showToastEvent.emit('Reservation updated.');
      ref.close();
    });
  }

  cancelRow(reservation: any) {
    const updated = {
      ...reservation,
      bookingStatus:  'cancelled',
      paymentStatus:  reservation.paymentStatus === 'paid' ? 'refunded' : reservation.paymentStatus,
    };
    this.resService.update(updated).pipe(first()).subscribe(
      (res: any) => {
        const i = this.reservations.findIndex(x => x.activityReservationId === res.data.activityReservation.activityReservationId);
        if (i > -1) this.reservations[i] = res.data.activityReservation;
        this.reservations = [...this.reservations];
        this.showToastEvent.emit('Reservation cancelled.');
      },
      (err: any) => this.alertService.error(err)
    );
  }

  private dialogConfig(reservation: any): MatDialogConfig {
    const cfg = new MatDialogConfig();
    cfg.panelClass = 'kbm-dialog-panel';
    cfg.maxWidth   = '680px';
    cfg.width      = '100%';
    cfg.data = {
      title:       reservation ? `${reservation.firstName} ${reservation.lastName}` : 'New Reservation',
      reservation,
      activities: this.activities,
    };
    return cfg;
  }

  /* ── Helpers ── */
  activityNames(activityIds: string): string[] {
    if (!activityIds) return [];
    return activityIds.split(',').filter(Boolean).map(id => {
      const a = this.activities.find(x => String(x.activityId) === id.trim());
      return a ? `${a.emoji || ''} ${a.name}` : '';
    }).filter(Boolean);
  }

  fmt$(n: number): string {
    return '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  fmtDate(s: string): string {
    if (!s) return '—';
    const [y, m, d] = s.split('-');
    return `${m}/${d}/${y}`;
  }
}
